import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {assertAndContinue} from 'Asserts';
import {BenchlingAppUiBlockType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import notifications from 'notifications';
import {FONT_PROXIMA_16, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Badge, {BadgeColor} from 'ui-library/Badge';
import {GRAY} from 'ui-library/Colors';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import {mapOverBlocks, flatMapOverBlocks} from '../lib';
import {SessionStatusContainer} from '../sessions/SessionStatusContainer';
import SessionStatusPill from '../sessions/SessionStatusPill';
import {UiBlockWrapper} from '../ui-blocks/UiBlock';
import * as uiBlock from '../ui-blocks/UiBlockTypes';
import {GetCanvas} from './__generated/GetCanvas';
import {submitCanvas, BlockUserInput, PlainTextTableMap} from './CanvasDAO';
import {validateInputs} from './InputValidation';
import ValidationBanners, {ValidationMessage} from './ValidationBanners';

type BenchlingAppCanvas = ExtractByTypename<GetCanvas['benchlingAppCanvas'], 'BenchlingAppCanvasNode'>;

export interface CanvasCoreProps {
  canvas: BenchlingAppCanvas;
  refetchCanvases: () => void;
  enabledOverride: boolean;
  dataTestAttrs: DataTestAttrs;
}

export function updateBlocks(
  blocks: Array<uiBlock.UiBlock>,
  id: string,
  val: string | Array<string> | null
): Array<uiBlock.UiBlock> {
  function updateSingleBlock<T extends uiBlock.UiBlock>(block: T): T {
    if (uiBlock.isUserInputBlock(block) && block.id === id) {
      // TODO(BNCH-77206) refactor this function to always use `blockValue`
      if (uiBlock.isMultiValueUserInputBlock(block)) {
        return {
          ...block,
          value: val,
          blockValue: {
            ...block.blockValue,
            values: Array.isArray(val) ? val : [],
          },
        };
      }
      return {
        ...block,
        value: val,
      };
    }
    return block;
  }
  return mapOverBlocks(blocks, updateSingleBlock);
}

export function extractUserInputMapping(uiBlocks: Array<uiBlock.UiBlock>): Array<BlockUserInput> {
  const filterInputs = (block: uiBlock.UiBlock): Array<BlockUserInput> => {
    // TODO(BNCH-77206) update to always use `blockValue`
    if (uiBlock.isUserInputBlock(block)) {
      if (uiBlock.isMultiValueUserInputBlock(block)) {
        return [{id: block.userDefinedId, value: block?.blockValue?.values || null}];
      }
      return [{id: block.userDefinedId, value: block.value}];
    } else {
      return [];
    }
  };
  return flatMapOverBlocks(uiBlocks, filterInputs);
}

function getTableStatusKey(uiBlocks: Array<uiBlock.UiBlock>): string {
  let key: string = '';
  for (const block of uiBlocks) {
    if (block.type === BenchlingAppUiBlockType.TABLE) {
      const tableStatus = `${block.userDefinedId}-${block.datasetId}-${block.datasetUploadStatus}|`;
      key += tableStatus;
    }
  }
  return key;
}

export function CanvasCore(props: CanvasCoreProps): JSX.Element {
  const {id, modifiedAt, enabled, session} = props.canvas;
  // Lock canvas if linked resource is locked (props.enabledOverride), or user locks (enabled)
  const canvasEnabled = props.enabledOverride && enabled;
  const uiBlocks = props.canvas.uiBlocks as Array<uiBlock.UiBlock>;
  const [editedBlocks, setEditedBlocks] = useState(uiBlocks);
  const [awaitingSubmission, setAwaitingSubmission] = useState(true);
  const [plainTextTableMap, updatePlainTextTableMap] = useState<PlainTextTableMap>({});
  const [validationMessages, setValidationMessages] = useState<Array<ValidationMessage>>([]);
  const tableStatusKey = getTableStatusKey(uiBlocks);

  useEffect(() => {
    setEditedBlocks(uiBlocks);
    setAwaitingSubmission(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modifiedAt, tableStatusKey]);

  const updateBlock = (blockId: string, val: string): void => {
    setEditedBlocks(updateBlocks(editedBlocks, blockId, val));
  };

  async function handleCanvasSubmit(
    canvasId: string,
    buttonId: string,
    uiBlocks: Array<uiBlock.UiBlock>
  ): Promise<void> {
    const blockUserInputs = extractUserInputMapping(uiBlocks);
    const response = await submitCanvas(canvasId, {buttonId, blockUserInputs, plainTextTableMap});
    if (response.deliveryStatus === 'failure') {
      notifications.error('Could not reach application. Please contact Benchling support if issue persists.');
    }
  }

  const onTableUpdate = (
    plainTextTable: Array<Array<string>>,
    tableId: string,
    dataFrameName: string
  ): void => {
    updatePlainTextTableMap({...plainTextTableMap, [tableId]: {plainTextTable, dataFrameName}});
  };

  const onCanvasSubmit = (buttonId: string): void => {
    assertAndContinue(canvasEnabled, 'Unexpected to handle canvas submission on disabled canvas.');
    if (!validateInputs(editedBlocks, setValidationMessages)) {
      return;
    }
    setAwaitingSubmission(false);
    handleCanvasSubmit(id, buttonId, editedBlocks);
  };

  return (
    <div {...getTestProps(props.dataTestAttrs)}>
      {validationMessages.length > 0 && (
        <ValidationBanners
          validationMessages={validationMessages}
          setValidationMessages={setValidationMessages}
        />
      )}
      {session && <SessionStatusContainer session={session} />}
      {editedBlocks.map((block, i) => (
        <div key={i} className={css(styles.blockContainer)}>
          <UiBlockWrapper
            canvasId={id}
            block={block}
            onChangeValue={updateBlock}
            onCanvasSubmit={onCanvasSubmit}
            onTableUpdate={onTableUpdate}
            canvasEnabled={canvasEnabled && awaitingSubmission}
          />
        </div>
      ))}
    </div>
  );
}

const LegacyCanvas = (props: CanvasCoreProps): JSX.Element => {
  const forceDisable = !props.enabledOverride || props.canvas.featureOrNull == null;
  return (
    <LegacyCanvasShell canvas={props.canvas}>
      <CanvasCore {...props} enabledOverride={!forceDisable} />
    </LegacyCanvasShell>
  );
};

interface LegacyCanvasShellProps {
  canvas: BenchlingAppCanvas;
  children: React.ReactNode;
}

const LegacyCanvasShell = (props: LegacyCanvasShellProps): JSX.Element => {
  const {benchlingApp, session} = props.canvas;
  return (
    <div className={css(styles.canvas)}>
      <div className="u-lr-split">
        <div className={css(styles.canvasTitle)}>
          <div className={css(styles.appNameTitle)}>{benchlingApp.name}</div>
          <Badge color={BadgeColor.GRAY} className="u-tiny-spacing-left">
            App
          </Badge>
        </div>
        {session && <SessionStatusPill status={session.status} />}
      </div>
      {props.children}
    </div>
  );
};

export function DefaultCanvasPlaceholder({benchlingAppName}: {benchlingAppName: string}): JSX.Element {
  return (
    <div className={css(styles.canvas)}>
      <div className={css(styles.canvasTitle)}>
        <div className={css(styles.appNameTitle)}>{benchlingAppName}</div>
        <Badge color={BadgeColor.GRAY} className="u-tiny-spacing-left">
          App
        </Badge>
      </div>
      <div className={css(styles.blockContainer)}>
        <FieldLabel label="Waiting for app..." labelStyle={FieldLabel.STYLES.LABEL} />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  canvas: {
    padding: '16px',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: GRAY,
  },
  canvasTitle: {
    display: 'flex',
    alignItems: 'center',
  },
  appNameTitle: {
    ...FONT_PROXIMA_16,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  blockContainer: {
    marginTop: '20px',
    ':first-child': {
      marginTop: 0,
    },
  },
});

export default LegacyCanvas;
